import React from 'react';
import { CircleBellRing, Pie, RejectIcon, SendReminder } from '@icons';
import { StartCollectingPayment } from '@images';

import AmountInput from '@components/amount-input/AmountInput';
import CustomTable from '@components/custom-table/CustomTable';
import {
  ConfirmationModal,
  CountryCode,
  Dropdown,
  EmptyScreen,
  Icon,
  Input,
  Label,
  Spinner,
  TextArea,
  ThreeDotActionButton
} from '@components/index';
import { paymentTableColumn } from '@features/lead-management/constants/leadDetails';
import usePaymentTab from '@features/lead-management/hooks/usePaymentTab';
import MarkAsPaidModal from '@features/transactions/components/mark-as-paid/MarkAsPaidModal';
import PaymentDetails from '@features/transactions/components/payment-details/PaymentDetails';
import TransactionAmount from '@features/transactions/components/transaction-amount/TransactionAmount';
import UpdateDueModal from '@features/transactions/components/update-due-modal/UpdateDueModal';
import { markAsPaidOptionsInitialState } from '@features/transactions/constants';
import ModalV2 from '@layouts/modal/Modal.v2';
import { MemberTransactionType } from '@types';

import theme from '../../../../../themes/colors';

interface PaymentTabProps {
  leadId?: string;
}

const PaymentTab: React.FC<PaymentTabProps> = ({ leadId }) => {
  const {
    isOpen,
    selectedBranch,
    setSelectedBranch,
    selectedGroup,
    setSelectedGroup,
    amount,
    notes,
    formError,
    branchListOptions,
    groupListOptions,
    onAmountChange,
    onAmountBlur,
    onNotesChange,
    onNotesBlur,
    handleStartCollectingPayment,
    handleCloseModal,
    handleSendLink,
    isSendDisabled,
    isLeadQuickCollectLoading,
    leadName,
    leadCountryCode,
    leadMobileNumber,
    isPaymentListLoading,
    hasPaymentData,
    paginatedTableData,
    totalCount,
    disabledRowIds,
    getPaymentActions,
    getDownloadActions,
    markAsPaidOptions,
    setMarkAsPaidOptions,
    markAsPaidBranchId,
    handleCustomMarkAsPaid,
    handleMarkAsPaidSuccess,
    isReminderModalOpen,
    handleConfirmReminder,
    handleCancelReminder,
    isDisableModalOpen,
    handleConfirmDisable,
    handleCancelDisable,
    isEnablePartialPaymentModalOpen,
    handleConfirmEnablePartialPayment,
    handleCancelEnablePartialPayment,
    isEnablingSplitPayment,
    updateDueOptions,
    handleCancelUpdateDue,
    handleUpdateSelectedAction,
    handleDueModalSuccessUpdate,
    handleCustomClosePayment,
    isDiscountConfirmationModalOpen,
    handleDiscountConfirmationButtonClick,
    isApplyingDiscount,
    selectedUpdateDueRow,
    isPaymentDetailsModalOpen,
    handleRowClick,
    handleClosePaymentDetails,
    handlePaymentDetailsDownload,
    selectedPaymentDetailsRow,
    PAYMENT_MIN_AMOUNT,
    PAYMENT_MAX_AMOUNT,
    organizationId,
    page,
    setPage
  } = usePaymentTab(leadId);

  const tableData = paginatedTableData.map((item: any) => ({
    ...item,
    amount: (
      <TransactionAmount
        amount={item.amount.total_amount}
        paidAmount={item.amount.total_settled_amount}
        settlementStatus={item.amount.settlement_status}
        splitPayment={item.amount.is_split_payment}
        status={item.amount.status}
        isDisabled={item.amount.status === 'disabled'}
        id={item.amount.id}
        showTooltip
      />
    ),
    actions: (() => {
      if (item.actions.status === 'disabled') return null;
      if (item.actions.status === 'success')
        return (
          <ThreeDotActionButton
            id={item.id}
            actions={getDownloadActions(item.actions.row)}
            usePortal
          />
        );
      return (
        <ThreeDotActionButton
          id={item.id}
          actions={getPaymentActions(item.actions.row as MemberTransactionType)}
          usePortal
        />
      );
    })()
  }));

  const renderPaymentContent = () => {
    if (isPaymentListLoading) {
      return (
        <div className="flex h-150 items-center justify-center">
          <Spinner />
        </div>
      );
    }

    if (!hasPaymentData) {
      return (
        <div className="h-150">
          <EmptyScreen
            title="Start collecting payment"
            description="Send a payment link to your leads and receive payments instantly."
            emptyScreenImage={StartCollectingPayment}
            buttonLabel="Collect payment"
            leftIconName="note-icon"
            handleClick={handleStartCollectingPayment}
            variant="primary"
            className="!justify-start"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <CustomTable
          headers={paymentTableColumn}
          selectedRows={[]}
          handleSelect={handleRowClick}
          data={tableData}
          totalCount={totalCount}
          currentPage={page}
          onPageChange={(count: number) => setPage(count)}
          disabledRowIds={disabledRowIds}
          className="!pt-4"
        />
      </div>
    );
  };

  return (
    <div>
      {renderPaymentContent()}

      {isOpen && (
        <ModalV2
          title="Collect payment"
          onClose={handleCloseModal}
          className="w-128 !rounded-2xl bg-white"
          primaryButtonLabel="Send Link"
          primaryButtonClick={handleSendLink}
          primaryButtonDisabled={isSendDisabled}
          primaryButtonLoading={isLeadQuickCollectLoading}
          secondaryButtonLabel="Cancel"
          secondaryButtonClick={handleCloseModal}
          secondaryButtonDisabled={isLeadQuickCollectLoading}
          isClickOutsideRequired
        >
          <div className="flex flex-col gap-4 py-2">
            {/* Name */}
            <div>
              <Label label="Name" isRequired className="!mb-1" />
              <Input
                value={leadName}
                handleInputChange={() => {}}
                type="text"
                placeholder="Name"
                disabled
                inputStyle="!bg-cofeeGreyBlue100 !border-none !text-cofeeNeutral7"
              />
            </div>

            {/* Mobile Number */}
            <div>
              <Label label="Mobile number" isRequired className="!mb-1" />
              <div className="flex gap-3">
                <CountryCode
                  hasSelection={false}
                  countryCode={leadCountryCode}
                  setCountryCode={() => {}}
                  dropdownStyle="!bg-cofeeGreyBlue100 !border-none"
                  titleLabelStyle="!text-cofeeNeutral7"
                />
                <Input
                  className="w-full"
                  value={leadMobileNumber}
                  handleInputChange={() => {}}
                  placeholder="Enter phone number"
                  type="phone"
                  disabled
                  inputStyle="!bg-cofeeGreyBlue100 !border-none !text-cofeeNeutral7"
                />
              </div>
            </div>

            {/* Branch */}
            <div>
              <Label label="Branch" isRequired className="!mb-1" />
              <Dropdown
                filterOptions={branchListOptions}
                searchBarPlaceholder="Search by Branch name"
                currentFilter={selectedBranch?.value || ''}
                onSelectFilter={(option: string) => {
                  const branch = branchListOptions.find(
                    (b) => b.value === option
                  );
                  if (branch) {
                    setSelectedBranch(branch);
                  }
                }}
                placeholder="Select branch"
                buttonClassName="w-full h-[50px] !bg-white !text-cofeeNeutral6"
                optionsStyle="max-h-[200px] overflow-y-auto w-full"
                usePortal
              />
            </div>

            {/* Group */}
            <div>
              <Label label="Group" className="!mb-1" />
              <Dropdown
                filterOptions={groupListOptions}
                searchBarPlaceholder="Search by Group name"
                currentFilter={selectedGroup?.value || ''}
                onSelectFilter={(option: string) => {
                  const group = groupListOptions.find(
                    (g) => g.value === option
                  );
                  if (group) {
                    setSelectedGroup(group);
                  }
                }}
                placeholder="Select group"
                disabled={!selectedBranch}
                buttonClassName={`w-full h-[50px] ${
                  !selectedBranch
                    ? '!bg-cofeeGreyBlue100 !border-none'
                    : '!bg-white'
                }`}
                optionsStyle="max-h-[200px] overflow-y-auto w-full"
                usePortal
                onClear={() => setSelectedGroup(null)}
              />
            </div>

            {/* Amount */}
            <div>
              <Label label="Amount" isRequired className="!mb-1" />
              <AmountInput
                value={amount}
                onAmountChange={onAmountChange}
                handleInputBlur={onAmountBlur}
                error={!!formError?.amount}
                className="!bg-white"
              />
              <div className="mt-1 flex items-center gap-1 text-sm text-grey60">
                <Icon name="alert-circle" stroke={theme.subText} size="16" />
                Allowed amount is between {PAYMENT_MIN_AMOUNT} and{' '}
                {PAYMENT_MAX_AMOUNT}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label label="Notes" isRequired className="!mb-1" />
              <TextArea
                value={notes}
                onChange={onNotesChange}
                onBlur={() => onNotesBlur(notes)}
                placeholder="Add Notes : Eg:Admission Fee for batch 7"
                placeholderStyle="!text-cofeeNeutral6 !text-sm"
                error={!!formError?.notes}
                errorMessage={formError?.notes}
                inputStyle="!bg-white !text-content !text-base "
                rows={2}
              />
            </div>
          </div>
        </ModalV2>
      )}

      {markAsPaidOptions.id !== '' && (
        <MarkAsPaidModal
          isOpen={markAsPaidOptions.id !== ''}
          handleCancel={() =>
            setMarkAsPaidOptions(markAsPaidOptionsInitialState)
          }
          transaction={markAsPaidOptions}
          onSuccessUpdate={handleMarkAsPaidSuccess}
          branchId={markAsPaidBranchId}
          organizationId={organizationId}
          flow="orgLevel"
          onCustomMarkAsPaid={handleCustomMarkAsPaid}
        />
      )}

      {isReminderModalOpen && (
        <ConfirmationModal
          isOpen={isReminderModalOpen}
          title="Send Reminder"
          message="On confirming reminder will be sent"
          confirmButtonLabel="Send"
          className="w-78"
          handleCancel={handleCancelReminder}
          handleConfirm={handleConfirmReminder}
          ModalIcon={SendReminder}
        />
      )}

      {isDisableModalOpen && (
        <ConfirmationModal
          isOpen={isDisableModalOpen}
          title="Disable Payment Link?"
          message="Are you sure, you want to disable this payment link permanently?"
          confirmButtonLabel="Disable"
          confirmButtonLabelStyle="!text-errorLite3"
          className="w-78"
          handleCancel={handleCancelDisable}
          handleConfirm={handleConfirmDisable}
          ModalIcon={RejectIcon}
        />
      )}

      {isEnablePartialPaymentModalOpen && (
        <ConfirmationModal
          isOpen={isEnablePartialPaymentModalOpen}
          title="Enable partial payment?"
          message="Once enabled, users will be able to make partial payment for this payment request"
          confirmButtonLabel="Confirm"
          confirmButtonLabelStyle="text-primary"
          className="w-78"
          handleCancel={handleCancelEnablePartialPayment}
          handleConfirm={handleConfirmEnablePartialPayment}
          ModalIcon={Pie}
          isConfirmButtonDisabled={isEnablingSplitPayment}
          isCancelButtonDisabled={isEnablingSplitPayment}
        />
      )}

      {updateDueOptions.id !== '' && selectedUpdateDueRow && (
        <UpdateDueModal
          isOpen={updateDueOptions.id !== ''}
          handleCancel={handleCancelUpdateDue}
          transactionData={updateDueOptions}
          onSuccessUpdate={handleDueModalSuccessUpdate}
          handleUpdateSelectedAction={handleUpdateSelectedAction}
          tabName="lead-payment"
          branchId={selectedUpdateDueRow.branch_id || ''}
          organizationId={organizationId}
          orderId={selectedUpdateDueRow.id}
          onCustomClosePayment={handleCustomClosePayment}
        />
      )}

      {isDiscountConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isDiscountConfirmationModalOpen}
          title="Notify about discount?"
          message="Would you like us to send a message to the member informing about the discount?"
          titleStyle="!text-base !text-cofeeNeutral10 !font-semibold !mb-2"
          cancelButtonLabelStyle="!font-medium"
          confirmButtonLabelStyle="!font-medium"
          messageStyle="text-sm font-medium leading-[20px] tracking-[-0.2px]"
          confirmButtonLabel="Yes, notify"
          cancelButtonLabel="No, skip"
          ModalIcon={CircleBellRing}
          customModalIconStyle="size-12"
          className="max-w-400"
          cancelButtonStyle="py-[11.5px] !mb-[24px] !mt-[20px]"
          primaryButtonVariant="primary"
          secondaryButtonVariant="tertiary"
          confirmButtonStyle="text-primary !py-[11.5px] !mb-[24px] !mt-[20px]"
          handleCancel={() => handleDiscountConfirmationButtonClick(false)}
          handleConfirm={() => handleDiscountConfirmationButtonClick(true)}
          isConfirmButtonDisabled={isApplyingDiscount}
          isCancelButtonDisabled={isApplyingDiscount}
          wrapperClass="!bg-white"
        />
      )}

      {isPaymentDetailsModalOpen && selectedPaymentDetailsRow && (
        <PaymentDetails
          isOpen={isPaymentDetailsModalOpen}
          handleCancel={handleClosePaymentDetails}
          branchId={selectedPaymentDetailsRow.branch_id || ''}
          orderId={selectedPaymentDetailsRow.id}
          handleDownload={handlePaymentDetailsDownload}
          leadId={leadId}
        />
      )}
    </div>
  );
};

export default PaymentTab;
